<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { formatRegionalUnixDate } from 'shared/helpers/DateHelper';
import { cortexConversationControlsAPI } from 'dashboard/api/cortex';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import AdaptiveSelect from 'dashboard/components-next/adaptive-select/AdaptiveSelect.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import CortexResponseViewerDialog from 'dashboard/routes/dashboard/cortex/components/CortexResponseViewerDialog.vue';

const props = defineProps({
  conversationId: { type: Number, required: true },
  sendDisabled: { type: Boolean, default: false },
});
const emit = defineEmits(['approve', 'close']);
const { t, locale } = useI18n();
const route = useRoute();
const { formatMessage } = useMessageFormatter();
const suggestionId = ref(null);
const turns = ref([]);
const instruction = ref('');
const pastedImages = ref([]);
const isLoading = ref(false);
const isRefining = ref(false);
const isProcessingImages = ref(false);
const isAbandoning = ref(false);
const error = ref('');
const warning = ref('');
const isRecording = ref(false);
const isTranscribing = ref(false);
const templateVariableKeys = ref([]);
const privateEntityOptions = ref([]);
const entitySelections = ref({});
const baseUpdateAuthorization = ref({ available: false });
const dialogRef = ref(null);
let pastedImageSequence = 0;
let mediaRecorder;
let recordingStream;
let recordingTimeout;
let audioChunks = [];

const currentSuggestion = computed(() => turns.value.at(-1)?.content || '');
const hasStarted = computed(() => suggestionId.value !== null);
const entityTypeLabels = computed(() => ({
  invoice: t('CONVERSATION.REPLYBOX.CORTEX_CHAT.PRIVATE_FACTS.TYPES.invoice'),
  server: t('CONVERSATION.REPLYBOX.CORTEX_CHAT.PRIVATE_FACTS.TYPES.server'),
  plan: t('CONVERSATION.REPLYBOX.CORTEX_CHAT.PRIVATE_FACTS.TYPES.plan'),
  contact: t('CONVERSATION.REPLYBOX.CORTEX_CHAT.PRIVATE_FACTS.TYPES.contact'),
}));
const privateFieldLabels = computed(() => ({
  'customer.invoice.number': t(
    'CONVERSATION.REPLYBOX.CORTEX_CHAT.PRIVATE_FACTS.FIELDS.customer_invoice_number'
  ),
  'customer.invoice.amount': t(
    'CONVERSATION.REPLYBOX.CORTEX_CHAT.PRIVATE_FACTS.FIELDS.customer_invoice_amount'
  ),
  'customer.invoice.currency': t(
    'CONVERSATION.REPLYBOX.CORTEX_CHAT.PRIVATE_FACTS.FIELDS.customer_invoice_currency'
  ),
  'customer.invoice.due_at': t(
    'CONVERSATION.REPLYBOX.CORTEX_CHAT.PRIVATE_FACTS.FIELDS.customer_invoice_due_at'
  ),
  'customer.invoice.paid_at': t(
    'CONVERSATION.REPLYBOX.CORTEX_CHAT.PRIVATE_FACTS.FIELDS.customer_invoice_paid_at'
  ),
  'customer.invoice.status': t(
    'CONVERSATION.REPLYBOX.CORTEX_CHAT.PRIVATE_FACTS.FIELDS.customer_invoice_status'
  ),
  'customer.invoice.pending_amount': t(
    'CONVERSATION.REPLYBOX.CORTEX_CHAT.PRIVATE_FACTS.FIELDS.customer_invoice_pending_amount'
  ),
  'customer.invoice.pending_due_at': t(
    'CONVERSATION.REPLYBOX.CORTEX_CHAT.PRIVATE_FACTS.FIELDS.customer_invoice_pending_due_at'
  ),
  'customer.invoice.pending_since': t(
    'CONVERSATION.REPLYBOX.CORTEX_CHAT.PRIVATE_FACTS.FIELDS.customer_invoice_pending_since'
  ),
  'customer.server.name': t(
    'CONVERSATION.REPLYBOX.CORTEX_CHAT.PRIVATE_FACTS.FIELDS.customer_server_name'
  ),
  'customer.server.url': t(
    'CONVERSATION.REPLYBOX.CORTEX_CHAT.PRIVATE_FACTS.FIELDS.customer_server_url'
  ),
  'customer.server.email': t(
    'CONVERSATION.REPLYBOX.CORTEX_CHAT.PRIVATE_FACTS.FIELDS.customer_server_email'
  ),
  'customer.plan.name': t(
    'CONVERSATION.REPLYBOX.CORTEX_CHAT.PRIVATE_FACTS.FIELDS.customer_plan_name'
  ),
  'customer.plan.changed_at': t(
    'CONVERSATION.REPLYBOX.CORTEX_CHAT.PRIVATE_FACTS.FIELDS.customer_plan_changed_at'
  ),
  'customer.plan.amount': t(
    'CONVERSATION.REPLYBOX.CORTEX_CHAT.PRIVATE_FACTS.FIELDS.customer_plan_amount'
  ),
  'customer.contact.name': t(
    'CONVERSATION.REPLYBOX.CORTEX_CHAT.PRIVATE_FACTS.FIELDS.customer_contact_name'
  ),
  'customer.contact.email': t(
    'CONVERSATION.REPLYBOX.CORTEX_CHAT.PRIVATE_FACTS.FIELDS.customer_contact_email'
  ),
  'customer.contact.phone': t(
    'CONVERSATION.REPLYBOX.CORTEX_CHAT.PRIVATE_FACTS.FIELDS.customer_contact_phone'
  ),
}));
const privateEntityGroups = computed(() =>
  ['invoice', 'server', 'plan', 'contact']
    .filter(type =>
      templateVariableKeys.value.some(key =>
        key.startsWith(`customer.${type}.`)
      )
    )
    .map(type => {
      const entities = privateEntityOptions.value.filter(
        item => item.type === type
      );
      const label = entityTypeLabels.value[type];
      return {
        type,
        label,
        options: entities.map((entity, index) => ({
          value: entity.id,
          label:
            entity.fields.find(field =>
              /\.(number|name|url|email)$/.test(field.key)
            )?.raw_value || `${label} ${index + 1}`,
        })),
        selected: entities.find(
          entity => entity.id === entitySelections.value[type]
        ),
      };
    })
);
const needsEntitySelection = computed(() =>
  privateEntityGroups.value.some(group => !group.selected)
);
const fieldLabel = key => privateFieldLabels.value[key];
const sourceUrl = messageId =>
  `/app/accounts/${route.params.accountId}/conversations/${props.conversationId}?messageId=${messageId}`;
const updatePrivateContext = data => {
  templateVariableKeys.value = data.template_variable_keys || [];
  privateEntityOptions.value = data.private_entity_options || [];
  entitySelections.value = {};
};
const canRefine = computed(
  () =>
    instruction.value.trim() && !isRefining.value && !isProcessingImages.value
);
const quickInstructions = computed(() => [
  t('CONVERSATION.REPLYBOX.CORTEX_CHAT.QUICK_FORMAL'),
  t('CONVERSATION.REPLYBOX.CORTEX_CHAT.QUICK_FRIENDLY'),
  t('CONVERSATION.REPLYBOX.CORTEX_CHAT.QUICK_CONCISE'),
  t('CONVERSATION.REPLYBOX.CORTEX_CHAT.QUICK_STEPS'),
]);

const errorTranslationKeys = {
  missing: () => t('CONVERSATION.REPLYBOX.CORTEX_CHAT.ERRORS.missing'),
  expired: () => t('CONVERSATION.REPLYBOX.CORTEX_CHAT.ERRORS.expired'),
  refused: () => t('CONVERSATION.REPLYBOX.CORTEX_CHAT.ERRORS.refused'),
  revoked: () => t('CONVERSATION.REPLYBOX.CORTEX_CHAT.ERRORS.revoked'),
  no_active_agent: () =>
    t('CONVERSATION.REPLYBOX.CORTEX_CHAT.ERRORS.no_active_agent'),
  no_incoming_message: () =>
    t('CONVERSATION.REPLYBOX.CORTEX_CHAT.ERRORS.no_incoming_message'),
  cortex_chat_stale_conversation: () =>
    t(
      'CONVERSATION.REPLYBOX.CORTEX_CHAT.ERRORS.cortex_chat_stale_conversation'
    ),
  cortex_chat_thread_expired: () =>
    t('CONVERSATION.REPLYBOX.CORTEX_CHAT.ERRORS.cortex_chat_thread_expired'),
  cortex_chat_thread_not_active: () =>
    t('CONVERSATION.REPLYBOX.CORTEX_CHAT.ERRORS.cortex_chat_thread_not_active'),
  cortex_chat_unavailable: () =>
    t('CONVERSATION.REPLYBOX.CORTEX_CHAT.ERRORS.cortex_chat_unavailable'),
  cortex_processing_media_pending: () =>
    t(
      'CONVERSATION.REPLYBOX.CORTEX_CHAT.ERRORS.cortex_processing_media_pending'
    ),
  cortex_chat_image_context_expired: () =>
    t('CONVERSATION.REPLYBOX.CORTEX_CHAT.PRIVATE_FACTS.IMAGE_EXPIRED'),
  cortex_chat_image_context_invalid: () =>
    t('CONVERSATION.REPLYBOX.CORTEX_CHAT.PRIVATE_FACTS.IMAGE_EXPIRED'),
};

const errorMessage = errorCode => {
  const translation = errorTranslationKeys[errorCode];
  return translation
    ? translation()
    : t('CONVERSATION.REPLYBOX.CORTEX_CHAT.ERROR_START');
};

const openDialog = () => {
  if (typeof dialogRef.value?.open === 'function') dialogRef.value.open();
};

const pastedImageId = file => {
  pastedImageSequence += 1;
  return `${file.name}-${file.size}-${pastedImageSequence}`;
};

const startChat = async () => {
  isLoading.value = true;
  error.value = '';
  try {
    const { data } = await cortexConversationControlsAPI.replySuggestion(
      props.conversationId
    );
    if (!data.success) {
      error.value = errorMessage(data.error);
      return;
    }
    suggestionId.value = data.suggestion_id;
    updatePrivateContext(data);
    baseUpdateAuthorization.value = data.base_update_authorization || {
      available: false,
    };
    turns.value = [
      { id: data.suggestion_id, role: 'assistant', content: data.suggestion },
    ];
  } catch (requestError) {
    error.value = errorMessage(
      requestError?.response?.data?.error || requestError?.message
    );
  } finally {
    isLoading.value = false;
  }
};

const onPaste = event => {
  const images = Array.from(event.clipboardData?.files || []).filter(file =>
    ['image/png', 'image/jpeg', 'image/webp'].includes(file.type)
  );
  if (!images.length) return;
  event.preventDefault();
  pastedImages.value.push(
    ...images.map(file => ({
      file,
      id: pastedImageId(file),
      status: 'ready',
      contextId: null,
      confirmed: false,
    }))
  );
};

const releaseRecorder = () => {
  clearTimeout(recordingTimeout);
  recordingStream?.getTracks().forEach(track => track.stop());
  recordingStream = undefined;
  mediaRecorder = undefined;
  audioChunks = [];
  isRecording.value = false;
};

const transcribeAudio = async blob => {
  isTranscribing.value = true;
  try {
    const formData = new FormData();
    formData.append('audio', blob, 'cortex_chat-instruction.webm');
    formData.append('suggestion_id', suggestionId.value);
    const { data } = await cortexConversationControlsAPI.transcribeInstruction(
      props.conversationId,
      formData
    );
    if (!data.success) throw new Error(data.error);
    instruction.value = data.text;
  } catch {
    error.value = t('CONVERSATION.REPLYBOX.CORTEX_CHAT.ERROR_TRANSCRIBE');
  } finally {
    isTranscribing.value = false;
  }
};

const toggleRecording = async () => {
  if (isRecording.value) {
    mediaRecorder?.stop();
    return;
  }
  try {
    recordingStream = await navigator.mediaDevices.getUserMedia({
      audio: true,
    });
    mediaRecorder = new MediaRecorder(recordingStream);
    audioChunks = [];
    mediaRecorder.ondataavailable = event => audioChunks.push(event.data);
    mediaRecorder.onstop = async () => {
      const blob = new Blob(audioChunks, {
        type: mediaRecorder?.mimeType || 'audio/webm',
      });
      releaseRecorder();
      await transcribeAudio(blob);
    };
    mediaRecorder.start();
    isRecording.value = true;
    recordingTimeout = setTimeout(() => mediaRecorder?.stop(), 60_000);
  } catch {
    releaseRecorder();
    error.value = t('CONVERSATION.REPLYBOX.CORTEX_CHAT.ERROR_MICROPHONE');
  }
};

const removeImage = imageId => {
  pastedImages.value = pastedImages.value.filter(image => image.id !== imageId);
};

const confirmExternalVisual = imageId => {
  const image = pastedImages.value.find(item => item.id === imageId);
  if (image) image.confirmed = true;
};

const processPendingImages = async images => {
  const [image, ...remainingImages] = images;
  if (!image) return true;

  image.status = 'processing';
  const formData = new FormData();
  formData.append('image', image.file);
  formData.append('suggestion_id', suggestionId.value);
  formData.append('external_visual_confirmed', image.confirmed);
  const { data } = await cortexConversationControlsAPI.processCortexChatImage(
    props.conversationId,
    formData
  );
  if (!data.success) throw new Error(data.error);
  if (data.external_visual_confirmation_required) {
    image.status = 'confirmation_required';
    return false;
  }
  image.contextId = data.image_context_id;
  image.status = 'complete';
  return processPendingImages(remainingImages);
};

const processImages = async () => {
  isProcessingImages.value = true;
  try {
    const pendingImages = pastedImages.value.filter(
      image => image.status !== 'complete'
    );
    return await processPendingImages(pendingImages);
  } catch {
    const failed = pastedImages.value.find(
      image => image.status === 'processing'
    );
    if (failed) failed.status = 'failed';
    error.value = t('CONVERSATION.REPLYBOX.CORTEX_CHAT.ERROR_IMAGE');
    return false;
  } finally {
    isProcessingImages.value = false;
  }
};

const sendInstruction = async () => {
  error.value = '';
  warning.value = '';
  if (!(await processImages())) return;
  isRefining.value = true;
  const submittedInstruction = instruction.value.trim();
  try {
    const { data } = await cortexConversationControlsAPI.refineReplySuggestion(
      props.conversationId,
      {
        suggestion_id: suggestionId.value,
        instruction: submittedInstruction,
        image_context_ids: pastedImages.value
          .map(image => image.contextId)
          .filter(Boolean),
      }
    );
    if (!data.success) {
      error.value = errorMessage(data.error);
      return;
    }
    baseUpdateAuthorization.value = data.base_update_authorization || {
      available: false,
    };
    turns.value.push({
      id: `${data.turn_id}-instruction`,
      role: 'user',
      content: submittedInstruction,
    });
    turns.value.push({
      id: data.turn_id,
      role: 'assistant',
      content: data.suggestion,
    });
    updatePrivateContext(data);
    warning.value = data.warning
      ? t('CONVERSATION.REPLYBOX.CORTEX_CHAT.INSUFFICIENT_KNOWLEDGE')
      : '';
    instruction.value = '';
    pastedImages.value = [];
  } catch {
    error.value = t('CONVERSATION.REPLYBOX.CORTEX_CHAT.ERROR_REFINE');
  } finally {
    isRefining.value = false;
  }
};

const approve = async () => {
  try {
    const { data: resolution } =
      await cortexConversationControlsAPI.resolveReplySuggestion(
        props.conversationId,
        {
          suggestion_id: suggestionId.value,
          content: currentSuggestion.value,
          template_variable_keys: templateVariableKeys.value,
          entity_selections: entitySelections.value,
        }
      );
    const content = resolution.content || currentSuggestion.value;
    const { data } = await cortexConversationControlsAPI.approveReplySuggestion(
      props.conversationId,
      { suggestion_id: suggestionId.value, content }
    );
    if (!data.success) throw new Error(data.error);
    emit('approve', {
      content,
      suggestionId: suggestionId.value,
      approvalId: data.approval_id,
      baseUpdateAuthorization: baseUpdateAuthorization.value,
    });
  } catch (requestError) {
    error.value =
      requestError?.response?.data?.error === 'template_variables_unresolved'
        ? t('CONVERSATION.REPLYBOX.CORTEX_CHAT.PRIVATE_FACTS.UNRESOLVED')
        : t('CONVERSATION.REPLYBOX.CORTEX_CHAT.ERROR_APPROVE');
  }
};

const abandon = async () => {
  isAbandoning.value = true;
  try {
    await cortexConversationControlsAPI.abandonReplySuggestion(
      props.conversationId,
      { suggestion_id: suggestionId.value }
    );
    suggestionId.value = null;
    turns.value = [];
    instruction.value = '';
    pastedImages.value = [];
    emit('close');
  } catch {
    error.value = t('CONVERSATION.REPLYBOX.CORTEX_CHAT.ERROR_ABANDON');
  } finally {
    isAbandoning.value = false;
  }
};

const restoreChat = async () => {
  try {
    const { data } = await cortexConversationControlsAPI.cortexChatStatus(
      props.conversationId
    );
    if (!data.active || !data.turns?.length) return false;
    suggestionId.value = data.suggestion_id;
    updatePrivateContext(data);
    baseUpdateAuthorization.value = data.base_update_authorization || {
      available: false,
    };
    turns.value = data.turns.flatMap(turn => {
      if (turn.role === 'assistant') return [turn];
      return [
        {
          id: `${turn.id}-instruction`,
          role: 'user',
          content: turn.instruction,
        },
        { id: turn.id, role: 'assistant', content: turn.content },
      ];
    });
    return true;
  } catch {
    return false;
  }
};

watch(
  () => props.conversationId,
  () => {
    suggestionId.value = null;
    turns.value = [];
    instruction.value = '';
    pastedImages.value = [];
    updatePrivateContext({});
    nextTick(async () => {
      openDialog();
      if (!(await restoreChat())) startChat();
    });
  }
);

onBeforeUnmount(releaseRecorder);
onMounted(async () => {
  openDialog();
  if (!(await restoreChat())) startChat();
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    position="top"
    overflow-y-auto
    :title="$t('CONVERSATION.REPLYBOX.CORTEX_CHAT.TITLE')"
    :description="$t('CONVERSATION.REPLYBOX.CORTEX_CHAT.DESCRIPTION')"
    :show-confirm-button="false"
    :show-cancel-button="false"
    border-class="border-n-weak"
    @close="emit('close')"
  >
    <div
      class="flex max-h-[calc(100vh-14rem)] min-h-80 flex-col gap-4"
      aria-live="polite"
    >
      <div
        v-if="isLoading"
        class="flex flex-1 items-center justify-center gap-3 text-sm text-n-slate-11"
      >
        <span class="i-lucide-loader-2 size-5 animate-spin text-n-brand" />
        <span>{{ $t('CONVERSATION.REPLYBOX.SUGGESTION_LOADING') }}</span>
      </div>
      <div
        v-else-if="error && !hasStarted"
        class="flex flex-1 flex-col items-center justify-center gap-3 text-center"
      >
        <span class="i-lucide-circle-alert size-6 text-n-ruby-11" />
        <p class="text-sm text-n-slate-11">{{ error }}</p>
        <NextButton
          sm
          outline
          slate
          icon="i-lucide-rotate-cw"
          :label="$t('CONVERSATION.REPLYBOX.SUGGESTION_RETRY')"
          @click="startChat"
        />
      </div>
      <template v-else-if="hasStarted">
        <div class="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pr-1">
          <article
            v-for="turn in turns"
            :key="turn.id"
            class="rounded-lg border border-n-weak p-3 text-sm"
            :class="
              turn.role === 'assistant'
                ? 'bg-n-surface-2 text-n-slate-12'
                : 'bg-n-alpha-1 text-n-slate-11'
            "
          >
            <div
              v-if="turn.role === 'assistant'"
              v-dompurify-html="formatMessage(turn.content, false)"
              class="prose-bubble leading-relaxed"
            />
            <p v-else class="whitespace-pre-wrap leading-relaxed">
              {{ turn.content }}
            </p>
            <CortexResponseViewerDialog
              v-if="turn.role === 'assistant'"
              :content="turn.content"
            />
          </article>
        </div>
        <p v-if="warning" class="text-sm text-n-amber-11">{{ warning }}</p>
        <p v-if="error" class="text-sm text-n-ruby-9">{{ error }}</p>
        <section
          v-if="privateEntityGroups.length"
          class="space-y-3 rounded-lg border border-n-weak bg-n-surface-2 p-3 text-n-slate-12"
        >
          <h3 class="text-sm font-medium">
            {{ $t('CONVERSATION.REPLYBOX.CORTEX_CHAT.PRIVATE_FACTS.TITLE') }}
          </h3>
          <p class="text-xs text-n-slate-11">
            {{
              $t('CONVERSATION.REPLYBOX.CORTEX_CHAT.PRIVATE_FACTS.DESCRIPTION')
            }}
          </p>
          <div
            v-for="group in privateEntityGroups"
            :key="group.type"
            class="space-y-2"
          >
            <WithLabel
              :name="`cortex-entity-${group.type}`"
              :label="group.label"
            >
              <AdaptiveSelect
                :id="`cortex-entity-${group.type}`"
                v-model="entitySelections[group.type]"
                :options="group.options"
                :placeholder="
                  $t('CONVERSATION.REPLYBOX.CORTEX_CHAT.PRIVATE_FACTS.CHOOSE')
                "
                :aria-label="group.label"
              />
            </WithLabel>
            <dl v-if="group.selected" class="space-y-2 text-sm">
              <div
                v-for="(field, index) in group.selected.fields"
                :key="`${field.key}-${field.source_message_id || field.source_event_id}-${index}`"
              >
                <dt class="font-medium">{{ fieldLabel(field.key) }}</dt>
                <dd class="break-words text-n-slate-11">
                  {{ field.raw_value }}
                  <span v-if="field.value_state === 'missing'">
                    {{
                      $t(
                        'CONVERSATION.REPLYBOX.CORTEX_CHAT.PRIVATE_FACTS.MISSING'
                      )
                    }}
                  </span>
                  <span
                    v-if="field.value_state === 'ambiguous'"
                    class="text-n-amber-11"
                  >
                    {{
                      $t(
                        'CONVERSATION.REPLYBOX.CORTEX_CHAT.PRIVATE_FACTS.AMBIGUOUS'
                      )
                    }}
                  </span>
                  <a
                    v-if="field.source_message_id"
                    :href="sourceUrl(field.source_message_id)"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="ml-2 rounded-sm text-n-brand hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
                  >
                    {{
                      $t(
                        'CONVERSATION.REPLYBOX.CORTEX_CHAT.PRIVATE_FACTS.SOURCE'
                      )
                    }}
                  </a>
                </dd>
                <dd
                  v-if="field.source_snapshot_id"
                  class="mt-1 flex flex-wrap gap-x-1 text-xs text-n-slate-11"
                >
                  <span>{{
                    $t(
                      'CONVERSATION.REPLYBOX.CORTEX_CHAT.PRIVATE_FACTS.OFFICIAL_SOURCE'
                    )
                  }}</span>
                  <span>{{ field.source_label }}</span>
                  <time v-if="field.observed_at" :datetime="field.observed_at">
                    {{
                      formatRegionalUnixDate(
                        new Date(field.observed_at).getTime() / 1000,
                        locale
                      )
                    }}
                  </time>
                </dd>
                <dd
                  v-if="field.source_event_id"
                  class="mt-1 text-xs text-n-slate-11"
                >
                  <span>{{
                    $t(
                      'CONVERSATION.REPLYBOX.CORTEX_CHAT.PRIVATE_FACTS.IMAGE_SOURCE'
                    )
                  }}</span>
                  <blockquote
                    class="mt-1 whitespace-pre-wrap break-words border-l-2 border-n-weak pl-2"
                  >
                    {{ field.quote }}
                  </blockquote>
                </dd>
              </div>
            </dl>
          </div>
        </section>
        <div class="flex flex-wrap gap-2">
          <NextButton
            v-for="quickInstruction in quickInstructions"
            :key="quickInstruction"
            :label="quickInstruction"
            size="sm"
            variant="outline"
            color="slate"
            @click="instruction = quickInstruction"
          />
        </div>
        <div v-if="pastedImages.length" class="grid gap-2">
          <div
            v-for="image in pastedImages"
            :key="image.id"
            class="flex items-center justify-between rounded-lg border border-n-weak bg-n-surface-2 p-2 text-xs"
          >
            <span>{{
              image.file.name ||
              $t('CONVERSATION.REPLYBOX.CORTEX_CHAT.PASTED_IMAGE')
            }}</span>
            <div class="flex items-center gap-2">
              <span v-if="image.status === 'processing'">{{
                $t('CONVERSATION.REPLYBOX.CORTEX_CHAT.PROCESSING_IMAGE')
              }}</span>
              <NextButton
                v-if="image.status === 'confirmation_required'"
                :label="
                  $t(
                    'CONVERSATION.REPLYBOX.CORTEX_CHAT.CONFIRM_SENSITIVE_IMAGE'
                  )
                "
                size="sm"
                variant="outline"
                color="slate"
                @click="confirmExternalVisual(image.id)"
              />
              <NextButton
                :label="$t('CONVERSATION.REPLYBOX.CORTEX_CHAT.REMOVE_IMAGE')"
                size="sm"
                variant="outline"
                color="slate"
                @click="removeImage(image.id)"
              />
            </div>
          </div>
        </div>
        <TextArea
          v-model="instruction"
          class="min-h-24 w-full resize-y"
          :placeholder="
            $t('CONVERSATION.REPLYBOX.CORTEX_CHAT.INSTRUCTION_PLACEHOLDER')
          "
          :maxlength="2000"
          @paste="onPaste"
        />
      </template>
    </div>
    <template #footer>
      <div class="flex flex-wrap justify-between gap-2">
        <NextButton
          sm
          outline
          slate
          icon="i-lucide-x"
          :label="$t('DIALOG.BUTTONS.CANCEL')"
          @click="emit('close')"
        />
        <div v-if="hasStarted" class="flex flex-wrap justify-end gap-2">
          <NextButton
            :label="
              isRecording
                ? $t('CONVERSATION.REPLYBOX.CORTEX_CHAT.STOP_RECORDING')
                : $t('CONVERSATION.REPLYBOX.CORTEX_CHAT.RECORD')
            "
            :icon="isRecording ? 'i-lucide-square' : 'i-lucide-mic'"
            size="sm"
            variant="outline"
            color="slate"
            :is-loading="isTranscribing"
            @click="toggleRecording"
          />
          <NextButton
            :label="$t('CONVERSATION.REPLYBOX.CORTEX_CHAT.ABANDON')"
            icon="i-lucide-x"
            size="sm"
            variant="outline"
            color="slate"
            :is-loading="isAbandoning"
            @click="abandon"
          />
          <NextButton
            :label="$t('CONVERSATION.REPLYBOX.CORTEX_CHAT.REFINE')"
            icon="i-lucide-send"
            size="sm"
            :disabled="!canRefine"
            :is-loading="isRefining || isProcessingImages"
            @click="sendInstruction"
          />
          <NextButton
            :label="$t('CONVERSATION.REPLYBOX.CORTEX_CHAT.APPROVE')"
            icon="i-lucide-check"
            size="sm"
            :disabled="
              sendDisabled || !currentSuggestion || needsEntitySelection
            "
            @click="approve"
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>
